import React, { useState, useEffect, useCallback, useRef } from "react";
import "react-toastify/dist/ReactToastify.css"; //https://fkhadra.github.io/react-toastify/introduction/
import DeleteComponent from "../DeleteComponent";
import { DialogComponent } from "@syncfusion/ej2-react-popups";
import {
  createDocFromCollection,
  addOrUpdateDocFromPath,
  docRef,
  colRef,
} from "../../globals/services";
import ConfirmComponent from "../ConfirmComponent";
import { AddCircleOutline, CloudUpload } from "@mui/icons-material";
import { Button, CircularProgress, IconButton } from "@mui/material";
import { Box } from "@mui/system";
import SpDialog from "../../globals/SpDialog";
import { generateUniqueId } from "../../globals/helpers";

/**
 *
 * @param {object} props coming from the branding.js file
 * @returns
 */
const ClientsLogos = (props) => {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [logos, setLogos] = useState([]);
  const [state, setState] = useState(false);
  const [website, setWebsite] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [file, setFile] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [buttonText, setButtonText] = useState("Update Clients");
  const [isLinkingClients, setIsLinkingClients] = useState(false);
  const [linkResult, setLinkResult] = useState(null);
  const [uploadingLogoId, setUploadingLogoId] = useState(null);
  const fileInputRefs = useRef({});

  const vendorData = props.vendorData;
  const vendorId = props.vendorData?.id;
  let storage = props.firebase.getStorage();

  /**
   * Arrays of file and upload each of them to the firebase storage and in firestore
   * @param {Array} files array of files
   * @returns a list of object containing fileName, fileRef, uploadedDate
   */

  /**
   * when the input button is clicked it will open the file upload dialog so users can select multiple files
   *
   * @param {*} e event of the input event
   */

  /**
   * takes 3 strings and find the data to be deleted then remove the data from the firebase
   * @param {String} fileName the name of the file to be deleted
   * @param {String} i index of the file to be deleted
   * @param {String} docId id of the file to be deleted in firebase
   */

  const deleteDoc = async (id) => {
    const delDoc = await docRef(`vendors/${vendorId}/clients/${id}`);
    delDoc.delete();
  };

  /**
   * Handle logo upload for a specific client
   * @param {String} clientId - The ID of the client to update
   * @param {File} newFile - The new logo file to upload
   */
  const handleLogoUpload = async (clientId, newFile) => {
    if (!newFile) return;

    setUploadingLogoId(clientId);
    try {
      const clientData = logos.find((logo) => logo.id === clientId);
      if (!clientData) {
        throw new Error("Client not found");
      }

      // Upload file to vendor storage path
      const uploadRef = storage.ref(
        `vendors/${vendorId}/clients/${newFile.name}`
      );
      const snapshot = await uploadRef.put(newFile);
      const fileUrl = await snapshot.ref.getDownloadURL();

      // Update the client document
      const clientDocRef = docRef(`vendors/${vendorId}/clients/${clientId}`);
      await clientDocRef.update({
        fileRef: fileUrl,
        fileName: newFile.name,
        updatedDate: props.firebase.fire.FieldValue.serverTimestamp(),
      });

      // Also update the company document if companyRef exists
      if (clientData.companyRef) {
        try {
          const companyDoc = await clientData.companyRef.get();
          if (companyDoc.exists) {
            // Use update with dot notation - Firestore will create nested path if needed
            await companyDoc.ref.update({
              "profile.logoUrl": fileUrl,
              lastUpdatedTS: props.firebase.fire.FieldValue.serverTimestamp(),
            });
          }
        } catch (err) {
          console.error("Error updating company logo:", err);
          // If update fails, try using set with merge as fallback
          try {
            const companyDoc = await clientData.companyRef.get();
            if (companyDoc.exists) {
              const companyData = companyDoc.data();
              const existingProfile = companyData.profile || {};
              await companyDoc.ref.set(
                {
                  profile: {
                    ...existingProfile,
                    logoUrl: fileUrl,
                  },
                  lastUpdatedTS:
                    props.firebase.fire.FieldValue.serverTimestamp(),
                },
                { merge: true }
              );
            }
          } catch (fallbackErr) {
            console.error(
              "Error updating company logo (fallback):",
              fallbackErr
            );
          }
        }
      }

      // Reset file input
      if (fileInputRefs.current[clientId]) {
        fileInputRefs.current[clientId].value = "";
      }
    } catch (err) {
      console.error("Error uploading logo:", err);
      alert("Failed to upload logo. Please try again.");
    } finally {
      setUploadingLogoId(null);
    }
  };

  /**
   * Trigger file input click for a specific client
   * @param {String} clientId - The ID of the client
   */
  const triggerFileInput = (clientId) => {
    if (!fileInputRefs.current[clientId]) {
      // Create a hidden file input if it doesn't exist
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".png, .jpg, .jpeg";
      input.style.display = "none";
      input.onchange = (e) => {
        if (e.target.files[0]) {
          handleLogoUpload(clientId, e.target.files[0]);
        }
      };
      document.body.appendChild(input);
      fileInputRefs.current[clientId] = input;
      input.click();
    } else {
      fileInputRefs.current[clientId].click();
    }
  };

  /**
   * click the input button when the custom button is clicked
   */
  const handleImageChange = (e) => {
    e.preventDefault();
    setFile(e.target.files[0]);
  };

  function checkCompanyName() {
    return logos.filter((logo) => logo.companyName === companyName);
  }

  const validate = () => {
    if (file === "") {
      alert("Please select a file");
      return false;
    }
    if (companyName === "") {
      alert("Please enter a company name");
      return false;
    }
    if (website === "") {
      alert("Please enter a website");
      return false;
    }
    return true;
  };

  const onSubmit = async (e, id) => {
    if (!validate()) return;
    setState(false);
    e.preventDefault();

    setUploading(true);

    try {
      // upload file to vendor storage path
      const uploadRef = storage.ref(`vendors/${vendorId}/clients/${file.name}`);
      const snapshot = await uploadRef.put(file);
      const fileUrl = await snapshot.ref.getDownloadURL();

      // generate uniqueId
      const uniqueId = generateUniqueId(companyName, website);

      // certified program data
      const data = {
        website: website,
        companyName: companyName,
        fileRef: fileUrl,
        fileName: file.name,
        uniqueId,
        // uploadedDate: new Date(),
      };

      // check for existing program by uniqueId in vendor's certifiedPrograms subcol
      const vendorProgramsCol = colRef(`vendors/${vendorId}/clients`);
      const existingProgramByUniqueId = await vendorProgramsCol
        .where("uniqueId", "==", uniqueId)
        .limit(1)
        .get();

      let programDocRef;

      if (!existingProgramByUniqueId.empty) {
        // found by uniqueId -> update
        programDocRef = existingProgramByUniqueId.docs[0].ref;
        await programDocRef.update({
          ...data,
          updatedDate: props.firebase.fire.FieldValue.serverTimestamp(),
        });
      } else {
        // fallback: check by companyName
        let existingProgram = checkCompanyName();
        if (existingProgram.length > 0) {
          // update existing found by companyName
          const path = `vendors/${vendorId}/clients/${existingProgram[0].id}`;
          await addOrUpdateDocFromPath(path, {
            ...data,
            updatedDate: props.firebase.fire.FieldValue.serverTimestamp(),
          });
          programDocRef = docRef(path);
        } else {
          // create new program doc in vendor's certifiedPrograms subcol
          const newDocRef = vendorProgramsCol.doc();
          await newDocRef.set({
            ...data,
            uploadedDate: props.firebase.fire.FieldValue.serverTimestamp(),
          });
          programDocRef = newDocRef;
        }
      }

      // call the HubSpot check/create company API
      const hsResponse = await fetch(
        `${props.firebase.spryteCloudUrl("checkAndCreateCompanyOnHubSpotAPI")}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            domain: website || "",
            companyName: companyName || "",
            refPath: vendorData.vendorRef.path,
          }),
        }
      );
      const hsResult = await hsResponse.json();
      if (hsResult.error) throw new Error(hsResult.error);

      // if HubSpot returned an hsID, find the company in Firestore and link
      const hsID = Number(hsResult.hsID);
      if (hsID) {
        // check if the company already exists
        const companiesCol = colRef("companies");
        const existingCompanies = await companiesCol
          .where("hsID", "==", hsID)
          .limit(1)
          .get();

        if (!existingCompanies.empty) {
          const companyDoc = existingCompanies.docs[0];
          const companyRef = companyDoc.ref;

          // link program -> company
          await programDocRef.update({
            companyRef,
          });

          // link company -> vendor
          await companyRef.update({
            vendors: props.firebase.fire.FieldValue.arrayUnion(
              docRef(`vendors/${vendorId}`)
            ),
          });

          // Also update company logo if file was uploaded
          if (fileUrl) {
            try {
              const companyDoc = await companyRef.get();
              if (companyDoc.exists) {
                // Use update with dot notation - Firestore will create nested path if needed
                await companyRef.update({
                  "profile.logoUrl": fileUrl,
                  lastUpdatedTS:
                    props.firebase.fire.FieldValue.serverTimestamp(),
                });
              }
            } catch (err) {
              console.error("Error updating company logo:", err);
              // If update fails, try using set with merge as fallback
              try {
                const companyData = await companyRef
                  .get()
                  .then((doc) => doc.data());
                const existingProfile = companyData?.profile || {};
                await companyRef.set(
                  {
                    profile: {
                      ...existingProfile,
                      logoUrl: fileUrl,
                    },
                    lastUpdatedTS:
                      props.firebase.fire.FieldValue.serverTimestamp(),
                  },
                  { merge: true }
                );
              } catch (fallbackErr) {
                console.error(
                  "Error updating company logo (fallback):",
                  fallbackErr
                );
              }
            }
          }
        }
      }

      setUploading(false);
      return {
        ...data,
        hsCompany: hsResult,
        programId: programDocRef.id,
      };
    } catch (err) {
      console.error("onSubmit error:", err);
      setUploading(false);
    }
  };

  useEffect(() => {
    const collectionRef = colRef(`vendors/${vendorId}/clients`);
    let subscribe = collectionRef.onSnapshot(async (snapshot) => {
      let data = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const clientData = { id: doc.id, ...doc.data() };

          // Fetch company data if companyRef exists
          if (clientData.companyRef) {
            try {
              const companyDoc = await clientData.companyRef.get();
              if (companyDoc.exists) {
                const companyData = companyDoc.data();
                clientData.companyProfileSlug = companyData.profileSlug;
                clientData.companyLogoUrl =
                  companyData.profile?.logoUrl ||
                  companyData.crm?.data?.properties?.logo?.value;
                // Use company logo if available, otherwise use client logo
                if (clientData.companyLogoUrl && !clientData.fileRef) {
                  clientData.fileRef = clientData.companyLogoUrl;
                }
              }
            } catch (err) {
              console.error("Error fetching company data:", err);
            }
          }

          return clientData;
        })
      );
      setLogos(data);
      setLoading(false);
    });
    return subscribe;
  }, [vendorId]);

  // Cleanup file input refs on unmount
  useEffect(() => {
    return () => {
      Object.values(fileInputRefs).forEach((input) => {
        if (input && input.parentNode) {
          input.parentNode.removeChild(input);
        }
      });
    };
  }, []);

  const onClose = useCallback(() => {
    setState(false);
  }, []);

  // update vendor clients info in Firestore
  // invokes the updateVendorClients cloud function when the Update Clients button is clicked
  const updateVendorClients = async () => {
    setIsLoading(true);
    setButtonText("Updating Clients...");

    try {
      const response = await fetch(
        `${props.firebase.spryteCloudUrl("updateVendorClientsAPI")}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            vendorBio: vendorData.profile.bio || "",
            companyName: vendorData.companyName || "",
            techFocus: vendorData.techFocus || "",
            website: vendorData.website || "",
            companyURL: vendorData.website || "",
            vendorRefPath: vendorData.vendorRef.path,
            pitchDeck: props.pitchDecks || [],
          }),
        }
      );

      const result = await response.json();
      if (result.error) throw new Error(result.error);
      console.log("Clients updated:", result.message);
    } catch (error) {
      console.error("Error updating clients:", error);
    } finally {
      setIsLoading(false);
      setButtonText("Update Clients");
    }
  };

  // Link clients to their companies
  // invokes the linkClientToCompanyAPI cloud function to link existing clients
  const linkClientsToCompanies = async () => {
    setIsLinkingClients(true);
    setLinkResult(null);

    try {
      const response = await fetch(
        `${props.firebase.spryteCloudUrl("linkClientToCompanyAPI")}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            vendorRefPaths: [vendorData.vendorRef.path],
          }),
        }
      );

      const result = await response.json();
      if (result.error) throw new Error(result.error);

      setLinkResult({
        success: true,
        message: result.message,
        linked: result.linkedClients,
        skipped: result.skippedClients,
        failed: result.failedClients,
        total: result.totalClientsProcessed,
      });

      // Refresh the clients list if linking was successful
      if (result.linkedClients > 0) {
        // Trigger a refresh by updating state or reloading data
        window.location.reload(); // Simple refresh, or you can implement a more elegant refresh
      }
    } catch (error) {
      console.error("Error linking clients:", error);
      setLinkResult({
        success: false,
        message: error.message || "Failed to link clients",
      });
    } finally {
      setIsLinkingClients(false);
    }
  };

  return (
    <>
      <div className="contract_cont">
        <div className="ctr security">
          <div className="ctr_el_header resource-header">
            <div className="header-intro">
              <h3>Clients</h3>
              <AddCircleOutline
                className="icon"
                onClick={() => setState(true)}
              />

              {uploading ? (
                <CircularProgress color="primary" size="small" />
              ) : (
                <></>
              )}
            </div>

            <div className="content">
              <p className="lighter" style={{ marginTop: "-8.5px" }}>
                Upload your current and past client logos. They will be
                displayed on your Profile page and have a huge impact on sales
                conversion.
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  flexWrap: "nowrap",
                  width: "100%",
                  maxWidth: "350px",
                }}
              >
                <button
                  onClick={updateVendorClients}
                  disabled={isLoading}
                  className="content__sp_update-clients-btn"
                >
                  <img
                    src={"/Spryte-AI-1.png"}
                    alt="Spryte AI logo"
                    width={25}
                    height={25}
                  />
                  <span>{buttonText}</span>
                </button>
                <button
                  onClick={linkClientsToCompanies}
                  disabled={isLinkingClients}
                  className="content__sp_update-clients-btn"
                >
                  <img
                    src={"/Spryte-AI-1.png"}
                    alt="Spryte AI logo"
                    width={25}
                    height={25}
                  />
                  <span>
                    {isLinkingClients ? "Linking clients" : "Link Clients"}
                  </span>
                </button>
              </div>
              {linkResult && (
                <div
                  style={{
                    marginTop: "10px",
                    padding: "10px",
                    backgroundColor: linkResult.success ? "#d4edda" : "#f8d7da",
                    color: linkResult.success ? "#155724" : "#721c24",
                    borderRadius: "5px",
                    fontSize: "14px",
                  }}
                >
                  <strong>{linkResult.success ? "Success:" : "Error:"}</strong>{" "}
                  {linkResult.message}
                  {linkResult.success && linkResult.total !== undefined && (
                    <div style={{ marginTop: "5px", fontSize: "13px" }}>
                      Linked: {linkResult.linked} | Skipped:{" "}
                      {linkResult.skipped} | Failed: {linkResult.failed} |
                      Total: {linkResult.total}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="contract_bottom">
          {logos.length !== 0 ? (
            <table className="stable">
              <thead className="stable_header">
                <tr>
                  <th>Logo</th>
                  <th>Company Name</th>
                  <th>Company Domain</th>
                  <th>Uploaded Date</th>
                  <th>Upload Logo</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>
                {logos.map((logo, id) => {
                  const companyUrl = logo.companyProfileSlug
                    ? `https://sprytelabs.com/company/${logo.companyProfileSlug}`
                    : null;
                  const hasCompanyRef = !!logo.companyRef;

                  const handleRowClick = (e) => {
                    // Don't navigate if clicking on buttons or links
                    if (
                      e.target.closest("button") ||
                      e.target.closest("a") ||
                      e.target.closest("svg") ||
                      e.target.closest(".MuiIconButton-root")
                    ) {
                      return;
                    }
                    if (companyUrl) {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(companyUrl, "_blank", "noopener,noreferrer");
                    }
                  };

                  return (
                    <tr
                      key={`${logo.fileName}${id}`}
                      onClick={handleRowClick}
                      style={{
                        cursor: companyUrl ? "pointer" : "default",
                        opacity: hasCompanyRef ? 1 : 0.6,
                        backgroundColor: hasCompanyRef
                          ? "transparent"
                          : "#fafafa",
                      }}
                      onMouseEnter={(e) => {
                        if (companyUrl) {
                          e.currentTarget.style.backgroundColor = "#f0f7ff";
                          e.currentTarget.style.opacity = "1";
                        } else if (!hasCompanyRef) {
                          e.currentTarget.style.backgroundColor = "#f5f5f5";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (companyUrl) {
                          e.currentTarget.style.backgroundColor = "transparent";
                        } else if (!hasCompanyRef) {
                          e.currentTarget.style.backgroundColor = "#fafafa";
                          e.currentTarget.style.opacity = "0.6";
                        }
                      }}
                    >
                      <td>
                        {logo.fileRef ? (
                          <img
                            src={logo.fileRef}
                            alt={logo.companyName || "Client logo"}
                            style={{
                              maxWidth: "80px",
                              maxHeight: "50px",
                              objectFit: "contain",
                              borderRadius: "4px",
                            }}
                          />
                        ) : (
                          <span style={{ color: "#999" }}>No logo</span>
                        )}
                      </td>
                      <td>
                        {logo.companyName}
                        {!hasCompanyRef && (
                          <span
                            style={{
                              marginLeft: "8px",
                              fontSize: "0.85em",
                              color: "#999",
                              fontStyle: "italic",
                            }}
                            title="Not linked to a company"
                          >
                            (not linked)
                          </span>
                        )}
                      </td>
                      <td>{logo.website}</td>
                      <td>
                        {logo.uploadedDate?.toDate().toDateString() || "N/A"}
                      </td>
                      <td>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            triggerFileInput(logo.id);
                          }}
                          disabled={uploadingLogoId === logo.id}
                          color="primary"
                          size="small"
                          title="Upload new logo"
                          style={{ padding: "8px" }}
                        >
                          {uploadingLogoId === logo.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <CloudUpload />
                          )}
                        </IconButton>
                      </td>
                      <td>
                        <DeleteComponent
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteDoc(logo.id);
                          }}
                          id={logo.id}
                          delete={deleteDoc}
                          fileName={logo.fileName}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p>You do not have any uploaded clients logo.</p>
          )}
        </div>
      </div>

      <SpDialog open={state} onClose={onClose}>
        <div style={{ display: "block", padding: 10 }} className="d">
          <form className="brand_dialog-box__inputs column">
            <section className="column section client_info">
              <div className="client_upload column info_input domain " mb={5}>
                <label>Company Domain</label>
                <input
                  type="text"
                  onChange={(e) => setWebsite(e.target.value)}
                  value={props.website}
                  placeholder="www.facebook.com"
                />
              </div>

              <div className="client_upload column  info_input name" mb={5}>
                <label>Company Name</label>
                <input
                  type="text"
                  onChange={(e) => setCompanyName(e.target.value)}
                  value={props.companyName}
                  placeholder="e.g. Facebook"
                />
              </div>

              <div className="client_upload column info_input name">
                <label htmlFor="file">Add Logo:</label>
                <input
                  type="file"
                  id="file"
                  name="file"
                  accept=".png, .jpg, .jpeg"
                  onChange={(e) => handleImageChange(e)}
                />
              </div>
            </section>
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => {
                console.log("clicked");
                onSubmit(e);
              }}
            >
              Submit
            </Button>
            {/* <ConfirmComponent
                onSubmit={onSubmit}
                companyName={companyName}
                setState={setState}
              /> */}
          </form>
        </div>
      </SpDialog>
    </>
  );
};

export default ClientsLogos;
